import { storeParticipantAccess } from '@src/access/participantAccess';
import './ParticipantSelect.css';
import { InlineAds } from '@src/components/Ads/Ads';
import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { BoxInfo, CardInfo } from '@lub0v/santa-utils';
import {
  BoxSelectHero,
  Button,
  Container,
  HintError,
  ParticipantPickerList,
  ParticipantTableFill,
  Stack,
  showDialog,
  closeDialog,
} from '@src/components';
import { sortSelectCards } from '@src/pages/BoxPage/sortCards';
import { boxCardPath } from '@src/routing/routes';
import { apiUrlPath } from '@src/routing/urls';
import { santaClient } from '@src/state/client';
import { useAppDispatch } from '@src/state/hooks';
import { loadBox } from '@src/state/boxSlice';
import { loadSession } from '@src/state/sessionSlice';

export interface ParticipantSelectProps {
  info: BoxInfo;
  cards: CardInfo[];
  /** Invite key the visitor arrived with; the claim is made against it. */
  join: string;
  /**
   * The organizer following a card link. They can see the list but not take a
   * name from it — the server refuses, and v1 says so before they try.
   */
  isOrganizer?: boolean;
  /** Start a new card instead of claiming an existing name. */
  onJoinNew?: () => void;
}

/**
 * What someone who opened the shared link sees: the list of names in the game,
 * for them to say which one is theirs. Picking is irreversible — the card is
 * then tied to this browser — so it goes through a confirmation.
 */
export function ParticipantSelect({
  info,
  cards,
  join,
  isOrganizer = false,
  onJoinNew,
}: ParticipantSelectProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [pending, setPending] = useState<CardInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const named = useMemo(
    () => sortSelectCards(cards.filter((card) => card.username)),
    [cards],
  );
  const items = useMemo(
    () =>
      named.map((card) => ({
        key: String(card.id),
        username: card.username || '',
        hidden: card.username === 'unknown',
        picture: card.picture,
        avatar: card.avatar,
        taken: card.isVerified,
      })),
    [named],
  );
  const cardsById = useMemo(() => new Map(named.map((card) => [String(card.id), card])), [named]);

  const claim = useCallback(
    async function claim(card: CardInfo) {
      setError(null);
      try {
        const result = await santaClient.consumeCardAccess({
          key: info.box.key,
          cardKey: card.randomKey,
          token: '',
          join,
        });
        if (!result.ok || !result.data?.redirect) {
          throw new Error(result.error?.message ?? 'CARD_ACCESS_UNAVAILABLE');
        }
        storeParticipantAccess(info.box.key, card.randomKey, result.data);
        // Full-game exchange and home share a mounted route. Refresh membership
        // before navigating so its guard cannot send the new participant back.
        setPending(null);
        closeDialog();
        await dispatch(loadSession()).unwrap();
        await dispatch(loadBox({ key: info.box.key, join })).unwrap();
        navigate(apiUrlPath(result.data.redirect));
      } catch (cause) {
        setPending(null);
        closeDialog();
        setError(cause instanceof Error ? cause.message : 'CARD_ACCESS_UNAVAILABLE');
      }
    },
    [dispatch, info.box.key, join, navigate],
  );

  useEffect(() => {
    if (!pending) {
      return;
    }
    showDialog({
      title: t('box.select.confirm.title', { name: pending.username }),
      children: t('box.select.confirm.body'),
      cancel: t('box.select.confirm.no'),
      submit: t('box.select.confirm.yes'),
      onSubmit: () => claim(pending),
      onCancel: () => setPending(null),
      testId: 'box-select-confirm',
    });
  }, [claim, pending, t]);

  return (
    <>
      <Container width="measure" grow className={`box-select-page${info.box.mode === 'full' ? ' box-select-page--ground' : ''}`}>
        <Stack gap={8} wide className="box-select-page__stack" testId="box-select">
          <BoxSelectHero
            hideIdentity
            name={info.box.name}
            picture={info.box.picture}
            logo={info.box.logo}
            title={t('box.select.title')}
            lead={t(info.box.useNames ? 'box.select.lead' : 'box.select.hiddenNamesLead')}
            testId="box-select-hero"
          />

          <ParticipantTableFill viewportPage>
            <ParticipantPickerList
              items={items}
              disabled={isOrganizer || Boolean(info.box.isArchived)}
              onRecover={isOrganizer ? undefined : (key) => {
                const card = cardsById.get(key);
                if (card) navigate(boxCardPath(info.box.key, card.randomKey));
              }}
              onPick={(key) => {
                const card = cardsById.get(key);
                if (card) {
                  setPending(card);
                }
              }}
              testId="box-select-panel"
            />
          </ParticipantTableFill>

          <Stack gap={4} align="center" wide className="box-select-page__footer">
            <HintError
              error={isOrganizer ? 'CARD_ACCESS_ORGANIZER_CANNOT_CLAIM' : error}
              testId="box-select-error"
            />

            {!info.isActiveDraw && onJoinNew && !info.box.isArchived ? (
              <Button variant="secondary" onClick={onJoinNew} testId="box-select-join-new">
                {t('game.nav.join')}
              </Button>
            ) : null}

            <Button
              variant="ghost"
              className="box-select-page__missing-self"
              onClick={() => {
                showDialog({
                  title: t('box.select.missingSelfTitle'),
                  children: t('box.select.missingSelfBody'),
                  submit: t('dialog.ok'),
                  testId: 'box-select-missing-self',
                });
              }}
              testId="box-select-missing-self"
            >
              {t('box.select.missingSelf')}
            </Button>
          </Stack>
        </Stack>
      </Container>
      <InlineAds placement />
    </>
  );
}
